import {z} from 'zod';
import {FC, useState} from 'react';
import {toast} from 'react-toastify';
import {zodResolver} from '@hookform/resolvers/zod';
import {FormProvider, useForm} from 'react-hook-form';
import {VerdocsButton, VerdocsSpinner} from '@verdocs/web-sdk-react';
import {type IBrand, createBrand, VerdocsEndpoint} from '@verdocs/js-sdk';
import {track} from '../Managers/Analytics';
import {TextInput} from '../Components';
import {AppState} from '../AppState';
import {Dialog} from './Dialog';

const schema = z.object({
  key: z
    .string()
    .trim()
    .min(1, 'Key is required')
    .max(63)
    .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/, 'Lowercase letters, numbers, and hyphens only'),
  name: z.string().trim().max(100).optional(),
});

type SchemaType = z.infer<typeof schema>;

interface CreateBrandDialogProps {
  onClose: () => void;
  onCreated: (brand: IBrand) => void;
}

export const CreateBrandDialog: FC<CreateBrandDialogProps> = ({onClose, onCreated}) => {
  const organizationId = AppState.currentProfile?.organization_id || '';
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<SchemaType>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: {key: '', name: ''},
  });

  const onSubmit = (data: SchemaType) => {
    setSubmitting(true);
    return createBrand(VerdocsEndpoint.getDefault(), organizationId, {key: data.key, name: data.name || undefined})
      .then((r) => {
        setSubmitting(false);
        toast.success('Brand created');
        track('brand_created', {org_id: organizationId, source: 'scratch'});
        onCreated(r);
      })
      .catch((e: any) => {
        setSubmitting(false);
        toast.error(`Error creating brand: ${e.response?.data?.error || e.message}`);
      });
  };

  return (
    <Dialog onCancel={onClose}>
      <h1 className="sans-serif font-bold text-xl text-[#33364b] mb-4">Create Brand</h1>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <TextInput label="Key" placeholder="e.g. main, partner-portal" name="key" required />
          <p className="text-sm text-gray-500 mb-4">A unique identifier for this brand. Lowercase letters, numbers, and hyphens only.</p>

          <TextInput label="Name" placeholder="Display name for this brand" name="name" />

          <div className="flex flex-row items-center justify-end mt-10 gap-4">
            <VerdocsButton size="small" variant="outline" label="Cancel" onClick={onClose} disabled={submitting} />
            <VerdocsButton size="small" label="Create" type="submit" disabled={submitting} />
            {submitting && <VerdocsSpinner mode="dark" />}
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
};
